import { useAddContentActions } from 'components/WorkspaceEditor/hooks/useAddContentActions'
import { ADDITIONAL_ELEMENT_SUFFIX } from 'components/WorkspaceEditor/utils/workspaceEditorUtils'
import { useCallback } from 'react'
import {
    useNodeByContentId,
    useNodeMapStore,
} from '../store/NodeMapStore'
import { isEnterShortcut } from '../utils'
import { EDITOR_OPERATION_DELAY_MSEC } from './useKeyOperation'

/**
 * Hook to handle deactivation of editor and addition of new content node in the editor.
 * It checks if the parent node exists and if the action can be performed.
 * If conditions are met, it unfocus the editor and adds new content node after a sibling.
 *
 * @param {string | undefined} contentItemId - The ID of the content item.
 * @param {() => void} unFocusEditor - Function to unfocus the editor.
 * @param {(val: boolean) => void} [setShouldTriggerNewContent] - Optional function
 * to set the flag for triggering new content.
 * @returns {Object} - Contains handlers for deactivation and addition of content.
 */
export const useHandleDeactivateAndAddContent = (
    contentItemId: string | undefined,
    unFocusEditor: () => void,
    setShouldTriggerNewContent?: (val: boolean) => void
) => {
    const addContentActions = useAddContentActions()
    const parentNode = useNodeByContentId(contentItemId ?? '')

    /**
     * Helper to handle the "deactivate editor and add new content node" case for Enter key in plain text editor.
     * If the change detection indicates deactivation of current active editor, and the parentNode allows adding content node,
     * this will unfocus the editor and add new content node after the sibling.
     *
     * @param changeDetection - The result of detectContentChanges
     * @param parentNode - The parent node object
     * @param addContentActions - Actions for adding content
     * @param unFocusEditor - Function to unfocus the editor
     * @returns true if the handler performed the action, false otherwise
     */
    const handleDeactivateAndAddContentPlainText = useCallback(
        (
            event: React.KeyboardEvent<HTMLElement>,
            changeDetection: {
                shouldDeactivate?: boolean
                shouldRevertToPrevious?: boolean
                hasChanges: boolean
                shouldExecuteOptimistic: boolean
            }
        ): boolean => {
            if (!isEnterShortcut(event)) return false

            const shouldHandle =
                changeDetection.shouldDeactivate ||
                changeDetection.shouldRevertToPrevious
            const isKeyedContent = contentItemId?.includes(
                ADDITIONAL_ELEMENT_SUFFIX
            )
            const willSaveContent =
                changeDetection.hasChanges &&
                changeDetection.shouldExecuteOptimistic
            const hasParentNode =
                parentNode &&
                addContentActions.canAddContent(parentNode, 'KeyboardShortcut')

            // CRITICAL: Read from store directly to get current synchronous value
            // Using the hook would give stale values from previous render
            const storeState = useNodeMapStore.getState()
            const isSaveOperationInProgress = storeState.isSaveOperationInProgress
            const contentItemBeingSaved = storeState.contentItemBeingSaved

            // Check if a keyed element is being saved (parent call during keyed child save)
            const isKeyedElementBeingSaved =
                contentItemBeingSaved?.includes(ADDITIONAL_ELEMENT_SUFFIX) &&
                contentItemBeingSaved !== contentItemId

            // Don't add content if:
            // 1. Save operation is in progress
            // 2. About to save content (will trigger save)
            // 3. Editing keyed content
            // 4. Another keyed element is being saved (prevents parent from adding content while keyed child saves)
            // Let useAddContentAfterAction handle it after save completes
            const shouldSkipContentAddition =
                isSaveOperationInProgress ||
                willSaveContent ||
                isKeyedContent ||
                isKeyedElementBeingSaved

            if (shouldSkipContentAddition) {
                return false
            }

            if (shouldHandle && hasParentNode) {
                setTimeout(() => {
                    unFocusEditor()
                    addContentActions.addContentAfterSibling(parentNode)
                }, EDITOR_OPERATION_DELAY_MSEC)
                return true
            }
            return false
        },
        [
            addContentActions,
            parentNode,
            unFocusEditor,
            contentItemId,
        ]
    )

    /**
     * Helper to handle the "deactivate content and add new content node" case for Enter key in Replacement Options.
     * If the change detection indicates deactivation of current active editor, and the parentNode allows adding content node,
     * this will unfocus the editor and add new content node after the sibling.
     *
     * @param changeDetection - The result of detectContentChanges
     * @param parentNode - The parent node object
     * @param addContentActions - Actions for adding content
     * @param unFocusEditor - Function to unfocus the editor
     * @returns true if the handler performed the action, false otherwise
     */
    const handleDeactivateAndAddContentReplacementOption = useCallback(
        (
            event: React.KeyboardEvent<HTMLElement>,
            hasNoChanges: boolean
        ): boolean => {
            if (!isEnterShortcut(event)) return false

            const isKeyedContent = contentItemId?.includes(
                ADDITIONAL_ELEMENT_SUFFIX
            )
            const hasParentNode =
                parentNode &&
                addContentActions.canAddContent(parentNode, 'KeyboardShortcut')

            // CRITICAL: Read from store directly to get current synchronous value
            const storeState = useNodeMapStore.getState()
            const isSaveOperationInProgress = storeState.isSaveOperationInProgress
            const contentItemBeingSaved = storeState.contentItemBeingSaved

            // Check if a keyed element is being saved (parent call during keyed child save)
            const isKeyedElementBeingSaved =
                contentItemBeingSaved?.includes(ADDITIONAL_ELEMENT_SUFFIX) &&
                contentItemBeingSaved !== contentItemId

            // Don't add content if save operation is in progress OR editing keyed content
            // Let useAddContentAfterAction handle it after save completes
            // Note: For replacement options, !hasNoChanges means content will be saved
            const shouldSkipContentAddition =
                isSaveOperationInProgress ||
                !hasNoChanges ||
                isKeyedContent ||
                isKeyedElementBeingSaved

            if (shouldSkipContentAddition) {
                return false
            }

            if (hasNoChanges && hasParentNode) {
                setTimeout(() => {
                    unFocusEditor()
                    addContentActions.addContentAfterSibling(parentNode)
                }, EDITOR_OPERATION_DELAY_MSEC)
                return true
            }
            setShouldTriggerNewContent?.(false)
            return false
        },
        [
            addContentActions,
            parentNode,
            unFocusEditor,
            setShouldTriggerNewContent,
            contentItemId,
        ]
    )

    return {
        handleDeactivateAndAddContentPlainText,
        handleDeactivateAndAddContentReplacementOption,
    }
}
